/**
 * Fechas que el usuario escribe en presupuestos, y la etiqueta de la semana hábil.
 * El corte del sábado (13:00) lo aplica el servidor con created_at.
 */

export type RangoFecha = { desde: string; hasta: string };

type Partes = { y: number; m: number; d: number; hour: number; minute: number; weekday: number };

const WEEKDAY: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

function partesART(date: Date): Partes {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Argentina/Buenos_Aires",
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || "";
  const weekday = WEEKDAY[get("weekday")] ?? 0;
  return {
    y: Number(get("year")),
    m: Number(get("month")),
    d: Number(get("day")),
    hour: Number(get("hour")),
    minute: Number(get("minute")),
    weekday,
  };
}

function sumarDias(y: number, m: number, d: number, dias: number) {
  const dt = new Date(Date.UTC(y, m - 1, d + dias));
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function iso(y: number, m: number, d: number) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function fechaValida(day: number, month: number, year: number) {
  if (month < 1 || month > 12 || day < 1 || year < 2000) return false;
  const dt = new Date(Date.UTC(year, month - 1, day));
  return dt.getUTCFullYear() === year && dt.getUTCMonth() === month - 1 && dt.getUTCDate() === day;
}

function ddmm(isoFecha: string) {
  const [, m, d] = isoFecha.split("-");
  return `${d}/${m}`;
}

export function etiquetaRango(desde: string, hasta: string) {
  const larga = (valor: string) => {
    const [y, m, d] = valor.split("-");
    return `${d}/${m}/${y}`;
  };
  if (desde === hasta) return larga(desde);
  return `${larga(desde)} a ${larga(hasta)}`;
}

/** Lun 19/10 a vie 23/10 · sábado solo hasta las 13hs */
export function etiquetaSemanaHabil(ahora = new Date()) {
  const hoy = partesART(ahora);
  const lunes = sumarDias(hoy.y, hoy.m, hoy.d, -hoy.weekday);
  const viernes = sumarDias(lunes.y, lunes.m, lunes.d, 4);
  return `Lun ${ddmm(iso(lunes.y, lunes.m, lunes.d))} a vie ${ddmm(iso(viernes.y, viernes.m, viernes.d))} · sábado solo hasta las 13hs`;
}

export function rangoUltimos30(ahora = new Date()): RangoFecha {
  const hoy = partesART(ahora);
  const desde = sumarDias(hoy.y, hoy.m, hoy.d, -29);
  return { desde: iso(desde.y, desde.m, desde.d), hasta: iso(hoy.y, hoy.m, hoy.d) };
}

/**
 * 23/10/2026, 23-10-2026, 23/10 o 23-10. Sin año usa el año en curso de Argentina.
 * 10/2026 no es un día: queda para el calendario del listado.
 */
export function interpretarFechaBusqueda(raw: string, ahora = new Date()): RangoFecha | null {
  const q = raw.trim();
  const anio = partesART(ahora).y;
  const completa = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/.exec(q);
  const corta = /^(\d{1,2})[\/\-.](\d{1,2})$/.exec(q);
  const match = completa || corta;
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = completa ? Number(match[3]) : anio;
  if (!fechaValida(day, month, year)) return null;
  const dia = iso(year, month, day);
  return { desde: dia, hasta: dia };
}
